import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { ArrowLeft, Download, Package, Truck, QrCode, Loader2, Check, AlertCircle, ArrowRight, SkipForward, RotateCcw, User } from "lucide-react";
import { drawLabelToCanvas, generateQRCodeDataUrl, preloadLogoImage } from "@/lib/label-utils";
import { generateQRStickerZip } from "@/lib/qr-label-zip";

// Performance metrics from add-prepaid-user
interface PerformanceMetrics {
  total_ms: number;
  auth_verify_ms: number;
  user_lookup_or_create_ms: number;
  parallel_inserts_ms: number;
  welcome_email_ms: number;
  box_count: number;
  user_existed: boolean;
}

// Response from add-prepaid-user (Step 1)
interface UserCreationResponse {
  success: boolean;
  user_id: string;
  email_sent: boolean;
  message: string;
  boxes_created: Array<{ id: string; box_number: string }>;
  kit_size: "large" | "small";
  kit_count: number;
  user_address: {
    full_name: string;
    address_line1: string;
    address_line2: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
  };
  performance_metrics?: PerformanceMetrics;
}

// Response from generate-prepaid-shipping-labels (Step 3)
interface ShippingLabelsResponse {
  success: boolean;
  outbound_kit_count: number;
  return_label_count: number;
  download_url: string;
  generation_time_ms: number;
  outbound_tracking?: Array<{ tracking_number: string; tracking_url: string; shipment_id: string }>;
  return_tracking?: Array<{ box_number: string; tracking_number: string; tracking_url: string }>;
}

type WizardStep = 1 | 2 | 3;
type StepStatus = "pending" | "in_progress" | "done" | "error" | "skipped";

export default function AdminAddPrepaid() {
  const navigate = useNavigate();
  
  // Wizard state
  const [wizardStep, setWizardStep] = useState<WizardStep>(1);
  const [isLoading, setIsLoading] = useState(false);
  
  // Step statuses
  const [step1Status, setStep1Status] = useState<StepStatus>("pending");
  const [step2Status, setStep2Status] = useState<StepStatus>("pending");
  const [step3Status, setStep3Status] = useState<StepStatus>("pending");
  const [step1Error, setStep1Error] = useState<string | null>(null);
  const [step2Error, setStep2Error] = useState<string | null>(null);
  const [step3Error, setStep3Error] = useState<string | null>(null);
  
  // Results
  const [userResult, setUserResult] = useState<UserCreationResponse | null>(null);
  const [shippingResult, setShippingResult] = useState<ShippingLabelsResponse | null>(null);
  const [qrLabelsBlob, setQrLabelsBlob] = useState<Blob | null>(null);
  
  // Preview
  const [labelPreview, setLabelPreview] = useState<string | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  
  // Form state
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [formData, setFormData] = useState({
    email: "",
    full_name: "",
    phone: "",
    address_line1: "",
    address_line2: "",
    city: "",
    state: "",
    zip: "",
    box_slots_purchased: "30",
    kit_size: "large" as "large" | "small",
    kit_count: "1",
  });

  const generateQRStickersClientSide = async (boxes: Array<{ id: string; box_number: string }>) => {
    return await generateQRStickerZip(boxes);
  };

  // ==========================================
  // STEP 1: Create user, profile, and boxes
  // ==========================================
  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setStep1Status("in_progress");
    setStep1Error(null);

    try {
      const { data: userData, error: userError } = await supabase.functions.invoke("add-prepaid-user", {
        body: {
          ...formData,
          box_slots_purchased: parseInt(formData.box_slots_purchased),
          kit_count: parseInt(formData.kit_count),
          send_welcome_email: sendWelcomeEmail,
        },
      });

      if (userError) throw userError;
      if (!userData.success) throw new Error(userData.error || "Failed to create user");

      const userResponse = userData as UserCreationResponse;
      setUserResult(userResponse);
      setStep1Status("done");
      setWizardStep(2);
      toast.success(`Created user ${formData.full_name} with ${userResponse.boxes_created.length} boxes`);

    } catch (error) {
      console.error("Step 1 error:", error);
      setStep1Status("error");
      setStep1Error(error instanceof Error ? error.message : "Unknown error");
      toast.error(error instanceof Error ? error.message : "Failed to create user");
    } finally {
      setIsLoading(false);
    }
  };

  // ==========================================
  // STEP 2: Generate QR stickers (client-side)
  // ==========================================
  const handleStep2Generate = async () => {
    if (!userResult) return;
    
    setIsLoading(true);
    setStep2Status("in_progress");
    setStep2Error(null);

    try {
      const { blob, elapsed } = await generateQRStickersClientSide(userResult.boxes_created);
      setQrLabelsBlob(blob);
      setStep2Status("done");
      setWizardStep(3);
      toast.success(`Generated ${userResult.boxes_created.length} QR stickers in ${elapsed}s`);
    } catch (error) {
      console.error("Step 2 error:", error);
      setStep2Status("error");
      setStep2Error(error instanceof Error ? error.message : "QR generation failed");
      toast.error("QR sticker generation failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStep2Skip = () => {
    setStep2Status("skipped");
    setWizardStep(3);
    toast.info("Skipped QR sticker generation");
  };

  // ==========================================
  // STEP 3: Generate shipping labels (edge function)
  // ==========================================
  const handleStep3Generate = async () => {
    if (!userResult) return;
    
    setIsLoading(true);
    setStep3Status("in_progress");
    setStep3Error(null);

    try {
      const { data: shippingData, error: shippingError } = await supabase.functions.invoke(
        "generate-prepaid-shipping-labels",
        {
          body: {
            user_id: userResult.user_id,
            boxes: userResult.boxes_created,
            kit_size: userResult.kit_size,
            kit_count: userResult.kit_count,
            user_address: userResult.user_address,
          },
        }
      );

      if (shippingError) throw shippingError;
      if (!shippingData.success) throw new Error(shippingData.error || "Failed to generate labels");

      const shippingResponse = shippingData as ShippingLabelsResponse;
      setShippingResult(shippingResponse);
      setStep3Status("done");
      toast.success(`Generated ${shippingResponse.outbound_kit_count} kit labels + ${shippingResponse.return_label_count} return labels`);
    } catch (error) {
      console.error("Step 3 error:", error);
      setStep3Status("error");
      setStep3Error(error instanceof Error ? error.message : "Shipping label generation failed");
      toast.error("Shipping label generation failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleStep3Skip = () => {
    setStep3Status("skipped");
    toast.info("Skipped shipping label generation");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // Sanitize phone number - strip non-digits
    if (name === "phone") {
      const sanitized = value.replace(/\D/g, "");
      setFormData((prev) => ({ ...prev, [name]: sanitized }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleDownloadShippingLabels = () => {
    if (shippingResult?.download_url) {
      window.open(shippingResult.download_url, "_blank");
    }
  };

  const handleDownloadQRStickers = () => {
    if (qrLabelsBlob) {
      const url = URL.createObjectURL(qrLabelsBlob);
      const link = document.createElement("a");
      link.download = `qr-stickers-${userResult?.user_id?.slice(0, 8) || "prepaid"}.zip`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    }
  };

  const generateLabelPreview = async () => {
    setIsLoadingPreview(true);
    setLabelPreview(null);
    try {
      const sampleBoxNumber = "ES-PREVIEW1";
      const appUrl = window.location.origin;
      const qrUrl = `${appUrl}/qr/${sampleBoxNumber}`;

      const qrDataUrl = await generateQRCodeDataUrl(qrUrl);
      const logoUrl = `${appUrl}/endless-storage-logo.png`;
      const logoImg = await preloadLogoImage(logoUrl);
      const preview = await drawLabelToCanvas(sampleBoxNumber, qrDataUrl, logoImg);

      setLabelPreview(preview);
    } catch (error) {
      console.error("Failed to generate preview:", error);
      toast.error("Failed to generate label preview");
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handleStartNewUser = () => {
    // Reset all state for a new user
    setWizardStep(1);
    setStep1Status("pending");
    setStep2Status("pending");
    setStep3Status("pending");
    setStep1Error(null);
    setStep2Error(null);
    setStep3Error(null);
    setUserResult(null);
    setShippingResult(null);
    setQrLabelsBlob(null);
    setFormData({
      email: "",
      full_name: "",
      phone: "",
      address_line1: "",
      address_line2: "",
      city: "",
      state: "",
      zip: "",
      box_slots_purchased: "30",
      kit_size: "large",
      kit_count: "1",
    });
    setSendWelcomeEmail(true);
  };

  const getStepStatusIcon = (status: StepStatus) => {
    switch (status) {
      case "done":
        return <Check className="h-4 w-4 text-green-600" />;
      case "error":
        return <AlertCircle className="h-4 w-4 text-destructive" />;
      case "in_progress":
        return <Loader2 className="h-4 w-4 animate-spin text-primary" />;
      case "skipped":
        return <SkipForward className="h-4 w-4 text-muted-foreground" />;
      default:
        return <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/30" />;
    }
  };

  const isComplete = step1Status === "done" && 
    (step2Status === "done" || step2Status === "skipped") && 
    (step3Status === "done" || step3Status === "skipped");

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-2xl mx-auto space-y-6">
        <Button variant="ghost" onClick={() => navigate("/admin")} className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Admin
        </Button>

        {/* Wizard Progress Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              {/* Step 1 */}
              <div className="flex items-center gap-2">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  step1Status === "done" ? "border-green-500 bg-green-50 dark:bg-green-950" :
                  step1Status === "error" ? "border-destructive bg-destructive/10" :
                  wizardStep === 1 ? "border-primary bg-primary/10" :
                  "border-muted-foreground/30"
                }`}>
                  {step1Status === "done" ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : step1Status === "error" ? (
                    <AlertCircle className="h-4 w-4 text-destructive" />
                  ) : step1Status === "in_progress" ? (
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  ) : (
                    <User className="h-4 w-4" />
                  )}
                </div>
                <div className="text-sm">
                  <div className="font-medium">Create User</div>
                  <div className="text-muted-foreground text-xs">User & boxes</div>
                </div>
              </div>

              <ArrowRight className="h-4 w-4 text-muted-foreground" />

              {/* Step 2 */}
              <div className="flex items-center gap-2">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  step2Status === "done" ? "border-green-500 bg-green-50 dark:bg-green-950" :
                  step2Status === "skipped" ? "border-muted-foreground/50 bg-muted" :
                  step2Status === "error" ? "border-destructive bg-destructive/10" :
                  wizardStep === 2 ? "border-primary bg-primary/10" :
                  "border-muted-foreground/30"
                }`}>
                  {getStepStatusIcon(step2Status) || <QrCode className="h-4 w-4" />}
                </div>
                <div className="text-sm">
                  <div className="font-medium">QR Stickers</div>
                  <div className="text-muted-foreground text-xs">Optional</div>
                </div>
              </div>

              <ArrowRight className="h-4 w-4 text-muted-foreground" />

              {/* Step 3 */}
              <div className="flex items-center gap-2">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                  step3Status === "done" ? "border-green-500 bg-green-50 dark:bg-green-950" :
                  step3Status === "skipped" ? "border-muted-foreground/50 bg-muted" :
                  step3Status === "error" ? "border-destructive bg-destructive/10" :
                  wizardStep === 3 ? "border-primary bg-primary/10" :
                  "border-muted-foreground/30"
                }`}>
                  {getStepStatusIcon(step3Status) || <Truck className="h-4 w-4" />}
                </div>
                <div className="text-sm">
                  <div className="font-medium">Shipping Labels</div>
                  <div className="text-muted-foreground text-xs">Optional</div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Results/Downloads Card - Show when any labels are ready */}
        {(shippingResult || qrLabelsBlob) && (
          <Card className="border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-800">
            <CardHeader>
              <CardTitle className="text-green-800 dark:text-green-200 flex items-center gap-2">
                <Package className="h-5 w-5" />
                Labels Ready for Download
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-3 gap-4 text-sm">
                {shippingResult && (
                  <>
                    <div className="flex items-center gap-2 p-3 bg-white dark:bg-background rounded-lg border">
                      <Truck className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{shippingResult.outbound_kit_count}</div>
                        <div className="text-muted-foreground text-xs">Outbound Kits</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 p-3 bg-white dark:bg-background rounded-lg border">
                      <Package className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{shippingResult.return_label_count}</div>
                        <div className="text-muted-foreground text-xs">Return Labels</div>
                      </div>
                    </div>
                  </>
                )}
                {qrLabelsBlob && userResult && (
                  <div className="flex items-center gap-2 p-3 bg-white dark:bg-background rounded-lg border">
                    <QrCode className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <div className="font-medium">{userResult.boxes_created.length}</div>
                      <div className="text-muted-foreground text-xs">QR Stickers</div>
                    </div>
                  </div>
                )}
              </div>

              {userResult?.boxes_created && userResult.boxes_created.length > 0 && (
                <div className="text-sm text-muted-foreground">
                  Box codes: {userResult.boxes_created.slice(0, 5).map((b) => b.box_number).join(", ")}
                  {userResult.boxes_created.length > 5 && ` +${userResult.boxes_created.length - 5} more`}
                </div>
              )}

              {/* Tracking Numbers Section */}
              {shippingResult?.outbound_tracking && shippingResult.outbound_tracking.length > 0 && (
                <div className="space-y-2 border-t pt-4">
                  <div className="text-sm font-medium">Outbound Kit Tracking</div>
                  <div className="flex flex-wrap gap-2">
                    {shippingResult.outbound_tracking.map((track, i) => (
                      <a
                        key={i}
                        href={track.tracking_url || `https://www.ups.com/track?tracknum=${track.tracking_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs bg-blue-100 dark:bg-blue-950 text-blue-800 dark:text-blue-200 px-2 py-1 rounded hover:bg-blue-200 dark:hover:bg-blue-900 transition-colors"
                      >
                        Kit {i + 1}: {track.tracking_number}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {shippingResult?.return_tracking && shippingResult.return_tracking.length > 0 && (
                <div className="space-y-2 border-t pt-4">
                  <div className="text-sm font-medium">Return Box Tracking</div>
                  <div className="flex flex-wrap gap-2">
                    {shippingResult.return_tracking.slice(0, 10).map((track, i) => (
                      <a
                        key={i}
                        href={track.tracking_url || `https://www.ups.com/track?tracknum=${track.tracking_number}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded hover:bg-accent transition-colors"
                      >
                        {track.box_number}: {track.tracking_number}
                      </a>
                    ))}
                    {shippingResult.return_tracking.length > 10 && (
                      <span className="text-xs text-muted-foreground">
                        +{shippingResult.return_tracking.length - 10} more
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Warning when labels ZIP appears empty */}
              {shippingResult && shippingResult.outbound_kit_count === 0 && shippingResult.return_label_count === 0 && (
                <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-amber-800 dark:text-amber-200">Labels ZIP appears empty</div>
                    <div className="text-xs text-amber-600 dark:text-amber-400">All PDF downloads failed. Try regenerating.</div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 border-amber-300 text-amber-700 hover:bg-amber-100 dark:border-amber-700 dark:text-amber-300 dark:hover:bg-amber-900"
                    onClick={() => {
                      setStep3Status("pending");
                      setShippingResult(null);
                      setStep3Error(null);
                      setWizardStep(3);
                    }}
                  >
                    <RotateCcw className="h-3 w-3" />
                    Regenerate
                  </Button>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <Button 
                  onClick={handleDownloadShippingLabels} 
                  className="gap-2"
                  disabled={!shippingResult?.download_url}
                >
                  <Download className="h-4 w-4" />
                  Shipping Labels (ZIP)
                </Button>
                <Button
                  onClick={handleDownloadQRStickers}
                  variant="outline"
                  className="gap-2"
                  disabled={!qrLabelsBlob}
                >
                  <QrCode className="h-4 w-4" />
                  QR Stickers (ZIP)
                </Button>
              </div>

              {/* Regenerate labels button (always available) */}
              {shippingResult && (shippingResult.outbound_kit_count > 0 || shippingResult.return_label_count > 0) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1 text-muted-foreground"
                  onClick={() => {
                    setStep3Status("pending");
                    setShippingResult(null);
                    setStep3Error(null);
                    setWizardStep(3);
                  }}
                >
                  <RotateCcw className="h-3 w-3" />
                  Regenerate Shipping Labels
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Performance Metrics Card - Show after Step 1 completes */}
        {userResult?.performance_metrics && (
          <Card className="border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-blue-800 dark:text-blue-200 flex items-center gap-2 text-sm">
                ⚡ Performance Metrics
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                <div className="bg-white dark:bg-background rounded-lg p-2 border">
                  <div className="font-mono font-medium text-base">
                    {userResult.performance_metrics.total_ms}ms
                  </div>
                  <div className="text-muted-foreground">Total Time</div>
                </div>
                <div className="bg-white dark:bg-background rounded-lg p-2 border">
                  <div className="font-mono font-medium text-base">
                    {userResult.performance_metrics.user_lookup_or_create_ms}ms
                  </div>
                  <div className="text-muted-foreground">
                    User {userResult.performance_metrics.user_existed ? "(found)" : "(created)"}
                  </div>
                </div>
                <div className="bg-white dark:bg-background rounded-lg p-2 border">
                  <div className="font-mono font-medium text-base">
                    {userResult.performance_metrics.parallel_inserts_ms}ms
                  </div>
                  <div className="text-muted-foreground">
                    Profile + Boxes ({userResult.performance_metrics.box_count})
                  </div>
                </div>
                {userResult.performance_metrics.welcome_email_ms > 0 && (
                  <div className="bg-white dark:bg-background rounded-lg p-2 border">
                    <div className="font-mono font-medium text-base">
                      {userResult.performance_metrics.welcome_email_ms}ms
                    </div>
                    <div className="text-muted-foreground">Welcome Email</div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Completion Card */}
        {isComplete && (
          <Card className="border-primary">
            <CardContent className="pt-6">
              <div className="text-center space-y-4">
                <div className="flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-950 flex items-center justify-center">
                    <Check className="h-6 w-6 text-green-600" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg">User Setup Complete</h3>
                  <p className="text-muted-foreground">
                    {userResult?.user_address.full_name} has been added with {userResult?.boxes_created.length} boxes
                  </p>
                </div>
                <Button onClick={handleStartNewUser} className="gap-2">
                  <User className="h-4 w-4" />
                  Add Another User
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 1: User Information Form */}
        {wizardStep === 1 && step1Status !== "done" && (
          <>
            {/* Label Preview Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <QrCode className="h-5 w-5" />
                  Label Preview
                </CardTitle>
                <CardDescription>
                  Preview the QR sticker design before generating labels
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Button onClick={generateLabelPreview} disabled={isLoadingPreview} variant="outline">
                  {isLoadingPreview ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    "Generate Preview"
                  )}
                </Button>

                {labelPreview && (
                  <div className="bg-muted rounded-lg p-4 flex items-center justify-center">
                    <img
                      src={labelPreview}
                      alt="Label Preview"
                      className="max-w-full h-auto shadow-md border rounded"
                      style={{ aspectRatio: "2/1" }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Main Form Card */}
            <Card>
              <CardHeader>
                <CardTitle>Step 1: Add User Information</CardTitle>
                <CardDescription>
                  Enter user details and shipping address. After creating the user, you can generate labels.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleStep1Submit} className="space-y-6">
                  {/* User Information */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                      User Information
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label htmlFor="full_name">Full Name</Label>
                        <Input
                          id="full_name"
                          name="full_name"
                          value={formData.full_name}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="col-span-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="col-span-2">
                        <Label htmlFor="phone">Phone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="4259856175"
                          maxLength={10}
                          required
                        />
                        <p className="text-sm text-muted-foreground mt-1">
                          Enter 10-digit phone number (digits only)
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Shipping Address */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                      Shipping Address
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label htmlFor="address_line1">Address Line 1</Label>
                        <Input
                          id="address_line1"
                          name="address_line1"
                          value={formData.address_line1}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div className="col-span-2">
                        <Label htmlFor="address_line2">Address Line 2 (Optional)</Label>
                        <Input
                          id="address_line2"
                          name="address_line2"
                          value={formData.address_line2}
                          onChange={handleChange}
                        />
                      </div>

                      <div>
                        <Label htmlFor="city">City</Label>
                        <Input
                          id="city"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="state">State</Label>
                        <Input
                          id="state"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                          required
                          maxLength={2}
                          placeholder="WA"
                        />
                      </div>

                      <div className="col-span-2">
                        <Label htmlFor="zip">ZIP Code</Label>
                        <Input
                          id="zip"
                          name="zip"
                          value={formData.zip}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  {/* Order Details */}
                  <div className="space-y-4">
                    <h3 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                      Order Details
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2">
                        <Label htmlFor="box_slots_purchased">Number of Box Slots</Label>
                        <Input
                          id="box_slots_purchased"
                          name="box_slots_purchased"
                          type="number"
                          min="1"
                          value={formData.box_slots_purchased}
                          onChange={handleChange}
                          required
                        />
                        <p className="text-sm text-muted-foreground mt-1">
                          This determines the number of return labels and QR stickers
                        </p>
                      </div>

                      <div className="col-span-2">
                        <Label>Kit Size (Outbound Box)</Label>
                        <RadioGroup
                          value={formData.kit_size}
                          onValueChange={(value: "large" | "small") =>
                            setFormData((prev) => ({ ...prev, kit_size: value }))
                          }
                          className="mt-2"
                        >
                          <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                            <RadioGroupItem value="large" id="kit-large" />
                            <Label htmlFor="kit-large" className="flex-1 cursor-pointer">
                              <span className="font-medium">Large</span>
                              <span className="text-muted-foreground ml-2">36" × 24" × 8" @ 5 lbs</span>
                            </Label>
                          </div>
                          <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                            <RadioGroupItem value="small" id="kit-small" />
                            <Label htmlFor="kit-small" className="flex-1 cursor-pointer">
                              <span className="font-medium">Small</span>
                              <span className="text-muted-foreground ml-2">36" × 24" × 4" @ 3 lbs</span>
                            </Label>
                          </div>
                        </RadioGroup>
                      </div>

                      <div className="col-span-2">
                        <Label htmlFor="kit_count">Number of Kits</Label>
                        <Input
                          id="kit_count"
                          name="kit_count"
                          type="number"
                          min="1"
                          value={formData.kit_count}
                          onChange={handleChange}
                          required
                        />
                        <p className="text-sm text-muted-foreground mt-1">
                          Number of outbound kit labels to generate
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Options */}
                  <div className="space-y-4 pt-4 border-t">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="send_welcome_email"
                        checked={sendWelcomeEmail}
                        onCheckedChange={(checked) => setSendWelcomeEmail(checked === true)}
                      />
                      <Label htmlFor="send_welcome_email" className="text-sm font-normal cursor-pointer">
                        Send welcome email with magic link
                      </Label>
                    </div>
                  </div>

                  {step1Error && (
                    <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm">
                      {step1Error}
                    </div>
                  )}

                  <Button type="submit" disabled={isLoading} className="w-full">
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating User...
                      </>
                    ) : (
                      <>
                        Create User & Boxes
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </>
        )}

        {/* STEP 2: QR Stickers */}
        {wizardStep === 2 && step2Status !== "done" && step2Status !== "skipped" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <QrCode className="h-5 w-5" />
                Step 2: Generate QR Stickers
              </CardTitle>
              <CardDescription>
                Generate printable QR code stickers for {userResult?.boxes_created.length} boxes
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {userResult && (
                <div className="p-4 bg-muted rounded-lg">
                  <div className="text-sm font-medium">User: {userResult.user_address.full_name}</div>
                  <div className="text-sm text-muted-foreground">
                    {userResult.boxes_created.length} boxes created
                  </div>
                </div>
              )}

              {step2Error && (
                <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  {step2Error}
                </div>
              )}

              <div className="flex gap-3">
                <Button onClick={handleStep2Generate} disabled={isLoading} className="flex-1">
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating...
                    </>
                  ) : step2Status === "error" ? (
                    <>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Retry
                    </>
                  ) : (
                    <>
                      <QrCode className="mr-2 h-4 w-4" />
                      Generate QR Stickers
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={handleStep2Skip} disabled={isLoading}>
                  <SkipForward className="mr-2 h-4 w-4" />
                  Skip
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* STEP 3: Shipping Labels */}
        {wizardStep === 3 && step3Status !== "done" && step3Status !== "skipped" && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="h-5 w-5" />
                Step 3: Generate Shipping Labels
              </CardTitle>
              <CardDescription>
                Generate {userResult?.kit_count} outbound kit labels and {userResult?.boxes_created.length} return labels
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {userResult && (
                <div className="p-4 bg-muted rounded-lg space-y-1">
                  <div className="text-sm font-medium">Ship to: {userResult.user_address.full_name}</div>
                  <div className="text-sm text-muted-foreground">
                    {userResult.user_address.address_line1}
                    {userResult.user_address.address_line2 && `, ${userResult.user_address.address_line2}`}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {userResult.user_address.city}, {userResult.user_address.state} {userResult.user_address.zip}
                  </div>
                </div>
              )}

              {step3Error && (
                <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-lg text-destructive text-sm flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  {step3Error}
                </div>
              )}

              <div className="flex gap-3">
                <Button onClick={handleStep3Generate} disabled={isLoading} className="flex-1">
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generating Labels...
                    </>
                  ) : step3Status === "error" ? (
                    <>
                      <RotateCcw className="mr-2 h-4 w-4" />
                      Retry
                    </>
                  ) : (
                    <>
                      <Truck className="mr-2 h-4 w-4" />
                      Generate Shipping Labels
                    </>
                  )}
                </Button>
                <Button variant="outline" onClick={handleStep3Skip} disabled={isLoading}>
                  <SkipForward className="mr-2 h-4 w-4" />
                  Skip
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
